import { FC, useState, useRef, useEffect } from "react";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import PageInner from "@app/shared/PageInner";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";
import {
    JobPerformanceDashboardRequest,
    JobPerformanceDashboardResponse,
} from "@app/operations/job-performance/jobPerformanceInterfaces";
import { QESButton } from "@app/shared/qes-standards/Button";
import JobPerformanceFilters, { DateRangeKey, ProjectType } from "@app/operations/job-performance/JobPerformanceFilters";
import JobPerformanceKpiCards from "@app/operations/job-performance/JobPerformanceKpiCards";
import { JobPerformanceActiveJobsTable } from "@app/operations/job-performance/JobPerformanceActiveJobsTable";
import { JobPerformanceClientRankingTable } from "@app/operations/job-performance/JobPerformanceClientRankingTable";
import { JobPerformanceProjectRunsTable } from "@app/operations/job-performance/JobPerformanceProjectRunsTable";
import { JobPerformanceZipProjectRunsTable } from "@app/operations/job-performance/JobPerformanceZipProjectRunsTable";
import { JobPerformanceSectionHeader } from "@app/operations/job-performance/JobPerformanceSectionHeader";
import { JobPerformanceSlowestJobTypesTable } from "@app/operations/job-performance/JobPerformanceSlowestJobTypesTable";
import { JobPerformanceSlowReportsTable } from "@app/operations/job-performance/JobPerformanceSlowReportsTable";
import { JobPerformanceQuarantineLogTable } from "@app/operations/job-performance/JobPerformanceQuarantineLogTable";

const makeDefaultFilters = (): JobPerformanceDashboardRequest => {
    const now = new Date();
    return {
        dateEnd: now.toISOString(),
        dateStart: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
};

const ACTIVE_JOBS_TOOLTIP    = "Currently queued, running, or failed jobs across all clients. Failed State (Min) shows how long a job has been in a failed state — for 'Failed — Action Required' rows this value is still accumulating until the job is manually re-queued. Click Refresh to update.";
const CLIENT_RANKING_TOOLTIP = "Top 10 clients ranked by average project run time using JobType 1002 (CustomAdequacyRegionSpecialtyGroupAccess) child jobs. Measured from MIN(DateQueued) to MAX(DateFinished) per ProjectRunConfigurationId. Quarantined runs (≥1,440 min) excluded from averages.";
const PROJECT_RUNS_TOOLTIP   = "Active project runs for the selected filters. Click any row to expand the full end-to-end phase breakdown.";
const ZIP_PROJECT_RUNS_TOOLTIP = "Zip Analysis project runs spanning every zip pipeline job type that fired for a run (project-run-config, region, zip-class-aggregate, finalizer, plus NCP / Provider Changes when applicable). Click any row to expand the full end-to-end phase breakdown.";
const SLOWEST_JOBS_TOOLTIP   = "Ranks job types by average total time. The Slower Than Avg multiplier = this job type's avg ÷ the overall avg across all job types. Higher values indicate the biggest optimization opportunities. Quarantined runs (≥1,440 min) excluded from averages.";
const SLOW_REPORTS_TOOLTIP   = "Ranks every report type by average generation time across Export, Volume Export, and Servicing Providers Volume Export jobs. The Job column shows which background job actually generates that report type. The Slower Than Avg multiplier = this report type's avg run time ÷ the overall avg across all report types. Quarantined runs (≥1,440 min) excluded from averages.";
const PROBLEM_JOBS_TOOLTIP   = "Jobs stuck in the queue for more than 120 minutes without starting (live data), and completed project runs that exceeded 24 hours. Both are excluded from all client averages, rankings, and outlier thresholds.";

const JobPerformance: FC = () => {
    const jobPerformanceService = useRef(new JobPerformanceService()).current;

    const [clients, setClients]                       = useState<{ clientId: number; name: string }[]>([]);
    const [filters, setFilters]                       = useState<JobPerformanceDashboardRequest>(makeDefaultFilters);
    const [committedFilters, setCommittedFilters]     = useState<JobPerformanceDashboardRequest>(makeDefaultFilters);
    const [dashboardData, setDashboardData]           = useState<JobPerformanceDashboardResponse | null>(null);
    const [loading, setLoading]                       = useState(false);
    const [dashboardError, setDashboardError]         = useState<string | null>(null);
    const [activeJobsRefreshToken, setActiveJobsRefreshToken] = useState(0);
    const [dateRangeKey, setDateRangeKey]             = useState<DateRangeKey>('7d');
    const [committedDateRangeKey, setCommittedDateRangeKey] = useState<DateRangeKey>('7d');
    const [projectType, setProjectType]               = useState<ProjectType>('County');
    const [committedProjectType, setCommittedProjectType] = useState<ProjectType>('County');

    useEffect(() => {
        jobPerformanceService.getClientFilterOptions()
            .then(setClients)
            .catch(err => console.error('Failed to load clients', err));
    }, []);

    useEffect(() => {
        setLoading(true);
        setDashboardError(null);
        jobPerformanceService.getDashboard(committedFilters)
            .then(setDashboardData)
            .catch(() => setDashboardError('Failed to load dashboard data. Please try again.'))
            .finally(() => setLoading(false));
    }, [committedFilters]);

    const handleSearch = () => {
        setCommittedFilters({ ...filters });
        setCommittedDateRangeKey(dateRangeKey);
        setCommittedProjectType(projectType);
    };

    // Project Type swaps which widgets are even on the page (KPI cards, Project Runs vs. Zip
    // Project Runs, tooltips) rather than just narrowing the data shown — so unlike the other
    // filters, it applies immediately instead of waiting for Search.
    const handleProjectTypeChange = (newProjectType: ProjectType) => {
        const updatedFilters: JobPerformanceDashboardRequest = {
            ...filters,
            isZipProjectType: newProjectType === 'Zip',
            // Job Type filtering to a single job type doesn't apply once zip's multi-job-type
            // pipeline is in view, so clear any previously-selected value.
            jobTypeId: newProjectType === 'County' ? filters.jobTypeId : undefined,
        };
        setProjectType(newProjectType);
        setFilters(updatedFilters);
        setCommittedProjectType(newProjectType);
        setCommittedFilters(updatedFilters);
    };

    const handleRefresh = () => {
        const refreshed = {
            ...makeDefaultFilters(),
            clientId: committedFilters.clientId,
            isZipProjectType: committedFilters.isZipProjectType,
            jobTypeId: committedFilters.jobTypeId,
        };
        setFilters(refreshed);
        setCommittedFilters(refreshed);
        setDateRangeKey('7d');
        setCommittedDateRangeKey('7d');
        setActiveJobsRefreshToken(t => t + 1);
    };

    const handleClear = () => {
        const defaults = makeDefaultFilters();
        setFilters(defaults);
        setCommittedFilters(defaults);
        setDateRangeKey('7d');
        setCommittedDateRangeKey('7d');
        setProjectType('County');
        setCommittedProjectType('County');
    };

    const clientName = clients.find(c => c.clientId === committedFilters.clientId)?.name ?? 'All Clients';

    return (
        <Page documentTitle="Job Performance">
            <Breadcrumbs template={BreadcrumbTemplateEnum.Operations} items={["Job Performance"]} />
            <PageInner>
                <PageHeader title="Job Performance" disableHR={true} />
                <PageSection>
                    <JobPerformanceKpiCards
                        clientName={clientName}
                        dateRangeKey={committedDateRangeKey}
                        kpis={dashboardData?.kpis ?? null}
                        loading={loading}
                        projectType={committedProjectType}
                    />
                    <JobPerformanceFilters
                        dateRangeKey={dateRangeKey}
                        filters={filters}
                        clients={clients}
                        loading={loading}
                        projectType={projectType}
                        onDateRangeKeyChange={setDateRangeKey}
                        onFiltersChange={setFilters}
                        onProjectTypeChange={handleProjectTypeChange}
                        onClear={handleClear}
                        onSearch={handleSearch}
                    />
                    {dashboardError && <p className="jp-error-message">{dashboardError}</p>}
                    <div className="jp-refresh-row">
                        <QESButton
                            qesType="primary"
                            qesTestId="job-performance__refresh-button"
                            onClick={handleRefresh}
                        >
                            Refresh
                        </QESButton>
                    </div>
                    {committedProjectType === 'County' && (
                        <div className="jp-section-table">
                            <JobPerformanceSectionHeader
                                title="Client Ranking — Slowest to Fastest"
                                tooltip={CLIENT_RANKING_TOOLTIP}
                            />
                            <JobPerformanceClientRankingTable
                                data={dashboardData?.clientRankings ?? []}
                                filters={committedFilters}
                                loading={loading}
                            />
                        </div>
                    )}

                    {committedProjectType === 'Zip' ? (
                        <div className="jp-section-table">
                            <JobPerformanceSectionHeader
                                title="Zip Project Runs"
                                tooltip={ZIP_PROJECT_RUNS_TOOLTIP}
                            />
                            <JobPerformanceZipProjectRunsTable filters={committedFilters} />
                        </div>
                    ) : (
                        <div className="jp-section-table">
                            <JobPerformanceSectionHeader
                                title="Project Runs"
                                tooltip={PROJECT_RUNS_TOOLTIP}
                            />
                            <JobPerformanceProjectRunsTable filters={committedFilters} />
                        </div>
                    )}

                    <div className="jp-section-table">
                        <JobPerformanceSectionHeader
                            title="Top 10 Slowest Job Types"
                            tooltip={SLOWEST_JOBS_TOOLTIP}
                        />
                        <JobPerformanceSlowestJobTypesTable
                            data={dashboardData?.slowestJobTypes ?? []}
                            loading={loading}
                        />
                    </div>

                    <div className="jp-section-table">
                        <JobPerformanceSectionHeader
                            title="Slow Reports"
                            tooltip={SLOW_REPORTS_TOOLTIP}
                        />
                        <JobPerformanceSlowReportsTable filters={committedFilters} />
                    </div>

                    <div className="jp-section-table">
                        <JobPerformanceSectionHeader
                            title="Active Jobs"
                            tooltip={ACTIVE_JOBS_TOOLTIP}
                        />
                        <JobPerformanceActiveJobsTable
                            filters={committedFilters}
                            refreshToken={activeJobsRefreshToken}
                        />
                    </div>

                    <div className="jp-section-table">
                        <JobPerformanceSectionHeader
                            title="Problem Jobs"
                            tooltip={PROBLEM_JOBS_TOOLTIP}
                        />
                        <JobPerformanceQuarantineLogTable
                            filters={committedFilters}
                            refreshToken={activeJobsRefreshToken}
                        />
                    </div>
                </PageSection>
            </PageInner>
        </Page>
    );
};

export default JobPerformance;
